/**
 * NbaCard - NBA candidate primitive (PROD-588 + PROD-590 RAG wiring).
 *
 * Phase 2 entry: surfaces a single NBA candidate as a Layer 4 action card
 * per ADR-001. Each card carries:
 *   - Verbatim title + 1 to 2 sentence clinical rationale.
 *   - Confidence pill bucketed via `priorityForConfidence` (high / medium /
 *     low) with the canonical NBA confidence tooltip copy on hover.
 *   - Sources block (HC3 + HC8 corpus-as-floor): when the optional
 *     `citations` prop carries a `CitationSet` (from PROD-590), the card
 *     renders the `<CitationStrip>` underneath the rationale. Each row
 *     opens a `<CitationDrawer>` showing the full chunk text plus
 *     provenance. When the resolver returned `source: 'empty'`, the card
 *     renders the inline empty-state ("No supporting evidence above
 *     threshold") instead of CitationStrip; the Override button stays
 *     available so the clinician can still act and capture the gap
 *     (HC3 invariant). When `citations` is undefined (early-render frame
 *     before resolution lands), the legacy fixture citation list renders
 *     so existing PROD-588 unit tests stay green.
 *   - Override button that opens the controlled `<OverrideModal>` (PROD-571)
 *     pre-filled with the candidate's pathway + step + nba ids.
 *   - Reduced-state badge + dimmed pill when `candidate.state === 'reduced'`,
 *     announced to screen readers via `role="status" aria-live="polite"`
 *     so the SR user is notified the moment confidence drops.
 *
 * HC8 no-over-suppression: the card NEVER hides itself based on state. A
 * `reduced` candidate stays in the rendered list at the lower-priority
 * bucket implied by the decayed confidence. The integration test in
 * `tests/clinical-desk-nba-flow.test.tsx` asserts the candidate count is
 * unchanged across the full Open / Fill / Submit cycle.
 *
 * Signal DS sourcing per COMPONENT-CONTRACTS §9 (NbaCard):
 *   - Card shell: Card (Action) [GAP: signal-ds-package] - tracked as G9
 *     in docs/SIGNAL-DS-CONTRIBUTIONS.md.
 *   - Confidence pill: Component (Badge variant `confidence-{high|medium|low}`)
 *     [GAP via G9].
 *   - Title + rationale: Token typography - DS-existing.
 *   - Citation list: Component (CitationFooter) [GAP via G9] - HC3
 *     mandates a visible source surface.
 *   - Override button: Component (Button variant `ghost`) - DS-existing.
 *
 * HC2: every color, spacing, radius value resolves through
 * src/styles/tokens.ts (no raw hex). HC5: zero em / en dashes anywhere in
 * the rendered copy or in this comment block - hyphens, commas, periods
 * only.
 *
 * HC6 a11y:
 *   - Card is `role="article"` with `aria-labelledby` pointing to the title.
 *   - Confidence pill carries `aria-label` with the full priority + numeric
 *     confidence reading so SR users hear the bucket and the score.
 *   - Override button is a real `<button>` with `aria-haspopup="dialog"`
 *     and `aria-expanded` mirroring the modal-open state.
 *   - Reduced-state badge uses `role="status"` and `aria-live="polite"` so
 *     SR users are notified the moment confidence drops post-override.
 */
import { useId, useState } from 'react';
import {
  priorityForConfidence,
  type NbaCandidate,
  type NbaPriority,
} from '../types/nba';
import type {
  Citation,
  NbaValidationTag,
} from '../data/synthetic-patients/types';
import { OverrideModal } from './OverrideModal';
import { CitationStrip } from './CitationStrip';
import { CitationDrawer } from './CitationDrawer';
import { ValidationTagBadge } from './ValidationTagBadge';
import type { OverrideEvent } from '../types/override';
import { NBA_CONFIDENCE_TOOLTIP_TEXT } from '../constants/nba-tooltip';
import type { CitationSet } from '../services/citation-resolver';
import type { RetrievedChunk } from './../services/corpus-retrieval';

/**
 * Props contract per COMPONENT-CONTRACTS §9 NbaCard. The candidate carries
 * the canonical learning-loop record; the patient context is needed for
 * the optional `data-patient-id` attribute used by the integration tests
 * to scope candidate queries.
 */
export interface NbaCardProps {
  /** The NBA candidate as the store sees it. */
  candidate: NbaCardCandidate;
  /**
   * Active patient id, used for the `data-patient-id` attribute so tests
   * can scope candidate queries to the active chart.
   */
  patientId: string;
  /**
   * Submit handler invoked AFTER the OverrideModal completes its own
   * persist + telemetry pipeline. Receives the structured event so the
   * caller can chain through the learning-loop service (PROD-572).
   */
  onOverride: (event: OverrideEvent, candidate: NbaCardCandidate) => void;
  /**
   * Optional resolved corpus citation set from PROD-590 citation resolver.
   * When present, the card renders a `<CitationStrip>` (or the inline
   * empty-state per HC3) under the rationale. When undefined, the card
   * falls back to the legacy fixture citation list so PROD-588 unit
   * tests keep passing without a resolver injection.
   */
  citations?: CitationSet;
  /**
   * PROD-595 walkthrough anchor flag. When true, the card root carries
   * `data-walkthrough-anchor="nba-cards"` and the Override button
   * carries `data-walkthrough-anchor="override-flow"` so the demo
   * walkthrough coachmarks position correctly on the first NBA card
   * rendered in ClinicalDesk. Defaults to false (no anchor).
   */
  isWalkthroughAnchor?: boolean;
  className?: string;
}

/**
 * Card-facing candidate view. Extends the canonical `NbaCandidate` (the
 * learning-loop record) with the visual fields that come from the
 * synthetic-patient fixture (rationale, citations). The store seeds these
 * fields from the patient fixtures' NBA arrays.
 */
export interface NbaCardCandidate extends NbaCandidate {
  /** 1 to 2 sentence clinical rationale; verbatim from the fixture. */
  rationale: string;
  /** HC3 citation list. May be empty until PROD-590 lands retrieval. */
  citations: ReadonlyArray<Citation>;
  /**
   * Optional UAE-overlay validation tag (Wave 5 Phase A). When present,
   * the card renders a `<ValidationTagBadge>` next to the title. When
   * absent, no badge is rendered (Wave 5 Phase A "do not fabricate" rule).
   */
  validationTag?: NbaValidationTag;
}

const REDUCED_STATE_LABEL = 'REDUCED';
const REDUCED_STATE_HINT =
  'Confidence reduced after a clinician override. The suggestion still surfaces at lower priority for context-dependent re-evaluation.';
const NO_CITATIONS_PLACEHOLDER = 'No supporting citations yet';
const NO_EVIDENCE_ABOVE_THRESHOLD =
  'No supporting evidence above threshold. Clinician judgment required.';
const OVERRIDE_BUTTON_LABEL = 'Override';

const PRIORITY_LABEL: Record<NbaPriority, string> = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence',
};

/**
 * Token-driven palette per priority bucket. The `low` bucket also covers
 * the `reduced` visual state; that state additionally renders the
 * REDUCED status badge above the pill.
 */
const PRIORITY_PILL_CLASSES: Record<NbaPriority, string> = {
  high: 'bg-status-active-bg text-status-active-fg ring-status-active',
  medium:
    'bg-status-draft-bg text-status-draft-fg ring-status-draft',
  low: 'bg-warning-soft-bg text-warning-soft-fg ring-warning-soft-border',
};

/**
 * Format a `[0, 1]` confidence as a percentage string. Pure helper.
 * Rounded to the nearest integer percent.
 */
function formatConfidencePercent(confidence: number): string {
  const pct = Math.round(confidence * 100);
  return `${pct}%`;
}

/**
 * Build the confidence-pill `aria-label` so SR users hear the bucket plus
 * the numeric score (HC6).
 */
function buildPillAriaLabel(priority: NbaPriority, confidence: number): string {
  return `${PRIORITY_LABEL[priority]}, ${formatConfidencePercent(confidence)}`;
}

export function NbaCard({
  candidate,
  patientId,
  onOverride,
  citations,
  isWalkthroughAnchor = false,
  className,
}: NbaCardProps) {
  const titleId = useId();
  const rationaleId = useId();
  const overrideButtonId = useId();
  const [open, setOpen] = useState(false);
  const [drawerChunk, setDrawerChunk] = useState<RetrievedChunk | null>(null);

  // Recompute the priority from the live confidence on every render so a
  // post-override rerender always reflects the new bucket. The store keeps
  // priority + confidence in sync, but this defensive re-derive keeps the
  // visual contract honest even if a fixture drifts.
  const priority = priorityForConfidence(candidate.confidence);
  const isReduced = candidate.state === 'reduced';
  const pillTone = isReduced ? 'low' : priority;
  const pillLabel = buildPillAriaLabel(priority, candidate.confidence);

  const baseClasses =
    'flex flex-col gap-gutter-sm rounded-signal-md border bg-surface-base px-gutter-md py-gutter-md shadow-signal-card transition-opacity';
  const stateClasses = isReduced
    ? 'border-warning-soft-border opacity-80'
    : 'border-surface-border opacity-100';
  const composed = [baseClasses, stateClasses, className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <article
      role="article"
      aria-labelledby={titleId}
      aria-describedby={rationaleId}
      data-testid={`nba-card-${candidate.id}`}
      data-nba-id={candidate.id}
      data-tenant-id={candidate.tenantId}
      data-patient-id={patientId}
      data-priority={priority}
      data-state={candidate.state}
      data-walkthrough-anchor={isWalkthroughAnchor ? 'nba-cards' : undefined}
      className={composed}
    >
      <header className="flex items-start justify-between gap-gutter-sm">
        <div className="flex flex-col gap-1">
          {isReduced ? (
            <span
              role="status"
              aria-live="polite"
              data-testid={`nba-card-${candidate.id}-reduced-badge`}
              data-reduced-badge="true"
              title={REDUCED_STATE_HINT}
              className="inline-flex w-fit items-center gap-1 rounded-signal-pill bg-warning-strong-bg px-gutter-sm py-0.5 text-signal-xs font-bold uppercase tracking-wider text-warning-strong-fg ring-1 ring-inset ring-warning-strong-border"
            >
              {REDUCED_STATE_LABEL}
            </span>
          ) : null}
          {candidate.validationTag ? (
            <ValidationTagBadge
              tag={candidate.validationTag}
              anchorId={`nba-card-${candidate.id}`}
            />
          ) : null}
          <h3
            id={titleId}
            data-testid={`nba-card-${candidate.id}-title`}
            className="text-signal-base font-semibold text-text-primary"
          >
            {candidate.title}
          </h3>
        </div>
        <span
          data-testid={`nba-card-${candidate.id}-confidence-pill`}
          data-confidence-bucket={priority}
          data-confidence-tone={pillTone}
          aria-label={pillLabel}
          title={NBA_CONFIDENCE_TOOLTIP_TEXT}
          className={[
            'inline-flex items-center gap-1 rounded-signal-pill px-gutter-sm py-0.5 text-signal-xs font-semibold uppercase tracking-wide ring-1 ring-inset',
            PRIORITY_PILL_CLASSES[pillTone],
          ].join(' ')}
        >
          <span aria-hidden="true">
            {formatConfidencePercent(candidate.confidence)}
          </span>
        </span>
      </header>

      <p
        id={rationaleId}
        data-testid={`nba-card-${candidate.id}-rationale`}
        className="text-signal-sm text-text-secondary"
      >
        {candidate.rationale}
      </p>

      {citations === undefined ? (
        // Legacy path: no resolver wired (PROD-588 unit tests, isolated
        // renders). Render the fixture citations inline as before.
        <CitationList
          citations={candidate.citations}
          nbaId={candidate.id}
        />
      ) : citations.source === 'empty' ? (
        // HC3 invariant: card renders the inline empty state instead of
        // CitationStrip. The Override button stays available so the
        // clinician can act and capture the gap.
        <p
          role="status"
          aria-live="polite"
          data-testid={`nba-card-${candidate.id}-citations-empty-evidence`}
          data-citation-source="empty"
          data-empty-reason={citations.emptyReason ?? ''}
          className="text-signal-xs italic text-warning-strong-fg"
        >
          {NO_EVIDENCE_ABOVE_THRESHOLD}
        </p>
      ) : (
        <CitationStrip
          citations={citations}
          nbaId={candidate.id}
          onOpenChunk={(chunk) => setDrawerChunk(chunk)}
        />
      )}

      <CitationDrawer
        open={drawerChunk !== null}
        chunk={drawerChunk}
        onClose={() => setDrawerChunk(null)}
      />

      <footer className="flex items-center justify-end gap-gutter-sm pt-gutter-sm">
        <button
          id={overrideButtonId}
          type="button"
          data-testid={`nba-card-${candidate.id}-override-button`}
          data-walkthrough-anchor={isWalkthroughAnchor ? 'override-flow' : undefined}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen(true)}
          className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-md py-1 text-signal-sm font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
        >
          {OVERRIDE_BUTTON_LABEL}
        </button>
      </footer>

      <OverrideModal
        open={open}
        onClose={() => setOpen(false)}
        pathwayId={candidate.pathwayId}
        stepId={candidate.stepId}
        nbaId={candidate.id}
        nbaTitle={candidate.title}
        onSubmitted={(event) => {
          // The OverrideModal already persisted + emitted `override_event`.
          // This callback chains through the learning-loop service so the
          // store can apply the confidence decay (which itself emits
          // `nba_confidence_adjusted` per HC9 telemetry-real).
          onOverride(event, candidate);
        }}
      />
    </article>
  );
}

interface CitationListProps {
  citations: ReadonlyArray<Citation>;
  nbaId: string;
}

function CitationList({ citations, nbaId }: CitationListProps) {
  if (citations.length === 0) {
    return (
      <p
        data-testid={`nba-card-${nbaId}-citations-empty`}
        className="text-signal-xs italic text-text-tertiary"
      >
        {NO_CITATIONS_PLACEHOLDER}
      </p>
    );
  }
  return (
    <ul
      data-testid={`nba-card-${nbaId}-citations`}
      className="flex flex-col gap-1 text-signal-xs text-text-tertiary"
    >
      {citations.map((c) => (
        <li
          key={c.id}
          data-testid={`nba-card-${nbaId}-citation-${c.id}`}
          className="flex items-baseline gap-1"
        >
          <span aria-hidden="true">·</span>
          {c.url ? (
            <a
              href={c.url}
              target="_blank"
              rel="noreferrer noopener"
              className="underline hover:text-text-secondary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            >
              {c.title} ({c.year})
            </a>
          ) : (
            <span>{c.title} ({c.year})</span>
          )}
        </li>
      ))}
    </ul>
  );
}

// Constants + helpers exported for tests + downstream consumers.
export {
  REDUCED_STATE_LABEL,
  REDUCED_STATE_HINT,
  NO_CITATIONS_PLACEHOLDER,
  NO_EVIDENCE_ABOVE_THRESHOLD,
  OVERRIDE_BUTTON_LABEL,
  PRIORITY_LABEL,
  formatConfidencePercent,
  buildPillAriaLabel,
};
